/**
 * Générateur — Attendu 955 — « Construire des triangles à partir de données
 * partielles. » (libellé officiel, BO)
 *
 * Niveau : 5ème | Domaine : Espace et géométrie / Triangles
 * Mise en conformité avec la spécification `Attendu-5e/specifications-tanoed/5e/955.json`
 * (campagne 5e, 2026-09-21). La spécification est la seule référence.
 *
 * ── Plan de cartes (version finale) ──────────────────────────────────────────
 *
 *  Une carte par jeu de données, dans l'ordre de la graduation. Chaque carte
 *  ouvre un programme de construction dont la PALETTE est fermée et annoncée
 *  dans l'énoncé (objets_geometriques 3) : le choix des tâches impose la
 *  méthode sans que la consigne ait à la dicter.
 *
 *  1. sas        — niveau 1 : deux longueurs et l'angle compris (règle,
 *                  rapporteur, compas). QCM : de quel angle doit-il s'agir ?
 *  2. sss        — niveau 2 : trois longueurs (règle et compas, pas de
 *                  rapporteur). QCM : combien de positions pour le sommet ?
 *  3. asa        — niveau 3 : un côté et les deux angles adjacents (règle et
 *                  rapporteur, pas de compas). QCM : la marche à suivre.
 *  4. construire — niveau 4 : un côté, un angle adjacent, un angle opposé ; le
 *                  troisième angle est à CALCULER avant de construire (saisie
 *                  du calcul, puis construction dont la consigne ne redonne pas
 *                  la valeur calculée).
 *  5. aug        — niveau 5 : triangle particulier, données réduites par le
 *                  codage — isocèle en A (codage + angle au sommet + base) ou
 *                  équilatéral (codage + côté). QCM : que faire après [GU] ?
 *  6. rectangle  — niveau 6 : triangle rectangle, l'angle droit donné par un
 *                  codage. Il se construirait a l'equerre sur papier ; la palette
 *                  interactive n'en ayant pas, la consigne passe par le rapporteur
 *                  a 90 degres (arbitrage prof 2026-09-26).
 *  Niveau 7 (mélange) : `generate()` sans clé.
 *
 * ── Spécification ────────────────────────────────────────────────────────────
 *
 *  Bornes appliquées :
 *   - longueur d'un côté : entier de 3 à 9 cm ; la contrainte porte sur LES
 *     TROIS côtés, y compris celui qui est calculé (exclusion 4) ;
 *   - écart minimal entre deux longueurs : 1 cm entre deux longueurs DONNÉES,
 *     0,5 cm entre deux côtés quelconques hors triangles particuliers
 *     (exclusion 3) ;
 *   - mesure d'un angle donné : entier de 20 à 120 degrés (angle obtus compris,
 *     écart assumé n° 2) ;
 *   - somme des deux angles donnés : 40 à 160 degrés, donc troisième angle
 *     d'au moins 20 degrés ;
 *   - marge de l'inégalité triangulaire : 0,5 cm ;
 *   - proportion de triangles particuliers : 2 constructions sur 6 (0,33), dans
 *     [0,2 ; 0,4] ;
 *   - nombre de données fournies : 3, sauf le triangle équilatéral (2 : le
 *     codage plus le côté), cas que la spec prévoit explicitement ;
 *   - tolérance de report d'un angle : 2 degrés (`tolerance_deg` des tâches) ;
 *   - nombre d'invariants vérifiés : 3 indépendants par construction (cf. plus
 *     bas) ;
 *   - tolérance de tracé sur une longueur : 0,4 cm et non 0,2 (voir Non tranché).
 *  Exclusions : aucun triangle dégénéré (marge d'inégalité) ; aucun jeu de
 *   données incompatible ; aucun côté égal à un autre hors des cartes `aug` et
 *   `rectangle` ; aucune longueur hors [3 ; 9] ; aucune donnée superflue (la
 *   valeur calculée n'est JAMAIS redonnée dans les consignes des cartes 4 et 5) ;
 *   aucune validation reposant sur un seul nombre.
 *
 *  Erreurs visées → mots-clés (codes des distracteurs, `_meta.params`) :
 *   1. construire juste mais nommer les sommets dans le désordre — ne pas voir
 *      que « l'angle ABC » a son sommet à la lettre du milieu
 *      → `sommet_lettre_du_milieu` (sas_qcm_unique)
 *   2. tracer un triangle qui ressemble aux données, le troisième sommet à vue
 *      → `trace_a_vue` (sss_qcm_unique, asa_qcm_unique, aug_qcm_unique)
 *   3. reporter la longueur du mauvais côté
 *      → `longueur_mauvais_cote` (asa_qcm_unique)
 *   4. deux angles et le côté commun : demi-droites mal placées, aucune
 *      intersection utile
 *      → `demi_droites_mauvais_cote` (asa_qcm_unique)
 *   5. croire que les données déterminent une seule figure
 *      → `solution_unique_supposee` (sss_qcm_unique ; dit aussi dans la
 *        consigne de sss_programme_construction, écart assumé n° 3)
 *   6. confondre l'angle compris entre deux côtés et un angle opposé
 *      → `angle_oppose_confondu` (sas_qcm_unique, aug_qcm_unique, et
 *        `erreurVisee` de construire_texte)
 *   7. tenir la construction pour achevée sans tracer le triangle
 *      → `triangle_non_ferme` (sss_qcm_unique, aug_qcm_unique ; les côtés sont
 *        des tâches explicites dans toutes les constructions)
 *
 *  Graduation → cartes : 1 sas · 2 sss · 3 asa · 4 construire · 5 aug ·
 *   6 rectangle · 7 `generate()` sans clé. Réglage transverse « palette
 *   complète ou restreinte » : palette restreinte à la méthode visée, annoncée
 *   dans l'énoncé de chaque construction.
 *
 *  Trois invariants indépendants par construction (points_de_vigilance 1 et 2) :
 *   - sas       : [AB] pré-tracé (1), demi-droite d'angle en A (2), arc de
 *                 rayon AC (3) — le sommet C confirme (2) et (3) ensemble ;
 *   - sss       : [BC] pré-tracé (1), arc de rayon AB (2), arc de rayon AC (3) ;
 *   - asa / 4 / 5 : [BC] pré-tracé (1), demi-droite d'angle en B (2),
 *                 demi-droite d'angle en C (3) ;
 *   - équilatéral : [UG] pré-tracé (1) et les deux arcs de même rayon (2, 3) ;
 *   - rectangle : [AB] pré-tracé (1), perpendiculaire en A (2), arc de rayon
 *                 AC (3).
 *   Aucune tâche ne se contente d'un produit scalaire ni d'une aire : chaque
 *   tâche est une longueur (centre et rayon) ou une direction (degrés), donc
 *   invariante par déplacement, et comparée à sa propre tolérance homogène —
 *   12 px pour une longueur, 2 degrés pour un angle.
 *
 *  Non tranché (appliqué tel qu'écrit, signalé dans le rapport) :
 *   - `tolerance_de_trace_sur_une_longueur` = 0,2 cm : le scoring compare à
 *     12 px FIXES et le carreau de la grille du composant vaut 30 px, soit
 *     1 cm ; la tolérance effective est donc de 0,4 cm. La changer supposerait
 *     de toucher au scoring ou au composant de rendu, tous deux interdits. La
 *     valeur réelle est annoncée dans chaque énoncé, comme la spec l'exige.
 *   - Le régime « construction sur papier avec grille d'auto-contrôle » de
 *     `type_reponse` n'a pas de format dans l'application : seul le régime
 *     numérique est servi.
 *   - « Séparer le score géométrique du score de nommage » relève du scoring,
 *     non touché : chaque tâche est validée séparément par le composant.
 */

import {
    randInt, randomChoice, pickLettresSains,
    makeSaisie, makeQcm, makeProgrammeConstruction,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 955;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const meta = (payload, key, params) => {
    payload._meta.generatorKey = key;
    payload._meta.params = params;
    return payload;
};

const L_SAS = 'Deux longueurs et l\'angle compris';
const L_SSS = 'Trois longueurs';
const L_ASA = 'Un côté et les deux angles adjacents';
const L_CONSTRUIRE = 'Calculer puis construire';
const L_AUG = 'Le triangle AUG (du cadrage)';
const L_RECT = 'Triangle rectangle : l\'angle droit est codé';

// Bornes de la spécification.
const LONG_MIN = 3;
const LONG_MAX = 9;
const ANG_MIN = 20;
const ANG_MAX = 120;
const SOMME_MIN = 40;
const SOMME_MAX = 160;
const ECART_LONG = 1;      // cm, entre deux longueurs DONNÉES
const ECART_COTES = 0.5;   // cm, entre deux côtés quelconques (hors particuliers)
const MARGE_INEG = 0.5;    // cm, marge de l'inégalité triangulaire

// Figure : le carreau de la grille du composant vaut 30 px, donc 1 cm = 1 carreau.
const PX = 30;
const TOL = 12;            // px — tolérance FIXE du scoring (questionUtils)
const TOL_DEG = 2;         // ° — tolérance de report d'un angle (spec)
const TOL_CM = '0,4';      // TOL / PX, annoncé à l'élève
const PAD = 54;            // px — marge du viewBox autour de la figure
const COL_B = '#2563EB';   // bleu : traces de construction (arcs, demi-droites)
const COL_V = '#1D9E75';   // vert : sommet et côtés du triangle

const toRad = (d) => (d * Math.PI) / 180;
const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUVWXYZ'.split('');
const pickLetters = (n) => pickLettresSains(POOL_LETTRES, n);
const arr2 = (x) => Math.round(x * 100) / 100;

// Mots-clés des erreurs visées (table dans l'en-tête).
const E_SOMMET = 'sommet_lettre_du_milieu';
const E_VUE = 'trace_a_vue';
const E_LONGUEUR = 'longueur_mauvais_cote';
const E_DEMI_DROITES = 'demi_droites_mauvais_cote';
const E_UNE_SOLUTION = 'solution_unique_supposee';
const E_ANGLE_OPPOSE = 'angle_oppose_confondu';
const E_NON_FERME = 'triangle_non_ferme';

const ang = (x, y, z) => `$\\widehat{${x}${y}${z}}$`;

/** Phrase de palette et de tolérance, commune à toutes les constructions. */
const palette = (instruments) =>
    `<p><em>Instruments : ${instruments}. La figure est juste si chaque tracé tombe `
    + `à moins de ${TOL_CM} cm (et 2° pour un angle).</em></p>`;

/**
 * Les trois côtés tiennent-ils dans les bornes de la spec ?
 * `particulier` lève l'interdiction de deux côtés égaux (cartes aug, rectangle).
 */
const cotesValides = (a, b, c, particulier = false) => {
    const t = [a, b, c];
    if (t.some((x) => x < LONG_MIN - 1e-9 || x > LONG_MAX + 1e-9)) return false;
    const [p, q, r] = [...t].sort((x, y) => x - y);
    if (p + q < r + MARGE_INEG) return false;
    if (particulier) return true;
    return Math.abs(a - b) >= ECART_COTES
        && Math.abs(b - c) >= ECART_COTES
        && Math.abs(a - c) >= ECART_COTES;
};

// ═══════════════════════════════════════════════════════════
// ASSEMBLAGE D'UN PROGRAMME DE CONSTRUCTION
// ═══════════════════════════════════════════════════════════

/**
 * Assemble le payload à partir de coordonnées de travail quelconques (repère
 * écran, y vers le bas) : le viewBox est calculé pour contenir la figure
 * ENTIÈRE — points nommés, extrémités de segments et cercles complets — avec
 * PAD de marge, puis toutes les coordonnées y sont translatées. Les deux
 * points d'intersection d'une paire d'arcs restent donc visibles, ce que la
 * spécification demande (objets_geometriques 5).
 */
const assembler = ({ points, preShapes, tasks, texte, label, key, params }) => {
    const xs = [];
    const ys = [];
    const noter = (p) => { xs.push(p.x); ys.push(p.y); };
    points.forEach(noter);
    tasks.forEach((t) => {
        const s = t.shape;
        if (s.type === 'segment' || s.type === 'line') { noter(s.p1); noter(s.p2); }
        else if (s.type !== 'ray') { xs.push(s.cx - s.r, s.cx + s.r); ys.push(s.cy - s.r, s.cy + s.r); }
        (t.extraShapes || []).forEach((e) => { noter(e.p1); noter(e.p2); });
    });
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const dx = PAD - minX;
    const dy = PAD - minY;
    const vb = {
        w: Math.round(Math.max(...xs) - minX + 2 * PAD),
        h: Math.round(Math.max(...ys) - minY + 2 * PAD),
    };
    const mv = (p) => ({ ...p, x: arr2(p.x + dx), y: arr2(p.y + dy) });
    const mvShape = (s) => {
        if (s.type === 'segment' || s.type === 'line') return { ...s, p1: mv(s.p1), p2: mv(s.p2) };
        if (s.type === 'ray') return s;
        return { ...s, cx: arr2(s.cx + dx), cy: arr2(s.cy + dy) };
    };
    const payload = makeProgrammeConstruction({
        pxPerCm: PX,
        tolerance: TOL,
        viewBox: vb,
        points: points.map(mv),
        preShapes,
        tasks: tasks.map((t) => ({
            ...t,
            shape: mvShape(t.shape),
            ...(t.extraShapes ? { extraShapes: t.extraShapes.map(mvShape) } : {}),
        })),
    }, texte, opts(label));
    return meta(payload, key, { ...params, tolerance_de_trace_cm: Number(TOL_CM.replace(',', '.')) });
};

/** Tâche « trace l'arc de centre P et de rayon r cm ». */
const tacheArc = (id, P, rCm, consigne) => ({
    id,
    consigne,
    color: COL_B,
    shape: { type: 'circle', cx: P.x, cy: P.y, r: rCm * PX },
});

/** Tâche « place le rapporteur sur P et trace la demi-droite à d degrés ». */
const tacheRay = (id, nomP, angleDeg, consigne) => ({
    id,
    kind: 'angle_ray',
    consigne,
    color: COL_B,
    tolerance_deg: TOL_DEG,
    shape: { type: 'ray', from: nomP, angleDeg },
});

/** Tâche « place le sommet S » (cercle de rayon nul : un point). */
const tachePoint = (id, S, nom, consigne) => ({
    id,
    consigne,
    color: COL_V,
    shape: { type: 'circle', cx: S.x, cy: S.y, r: 0 },
    pointLabel: nom,
});

/** Tâche « trace le côté [PQ] ». */
const tacheSegment = (id, P, Q, consigne) => ({
    id,
    kind: 'segment',
    consigne,
    color: COL_V,
    shape: { type: 'segment', p1: P, p2: Q },
});

// ═══════════════════════════════════════════════════════════
// Carte 1 — sas (niveau 1) : deux longueurs et l'angle compris
// ═══════════════════════════════════════════════════════════

const REPLI_SAS = { b: 4, c: 6, alpha: 50 };

const tirerSAS = () => {
    for (let i = 0; i < 300; i++) {
        const c = randInt(LONG_MIN, LONG_MAX);   // AB
        const b = randInt(LONG_MIN, LONG_MAX);   // AC
        if (Math.abs(b - c) < ECART_LONG) continue;
        const alpha = randInt(ANG_MIN, ANG_MAX); // angle en A, compris entre [AB] et [AC]
        const a = Math.sqrt(b * b + c * c - 2 * b * c * Math.cos(toRad(alpha)));
        if (!cotesValides(a, b, c)) continue;
        return { b, c, alpha, a };
    }
    const { b, c, alpha } = REPLI_SAS;
    return { b, c, alpha, a: Math.sqrt(b * b + c * c - 2 * b * c * Math.cos(toRad(alpha))) };
};

const genSasConstruction = () => {
    const { b, c, alpha, a } = tirerSAS();
    const [nA, nB, nC] = pickLetters(3);
    const A = { x: 0, y: 0 };
    const B = { x: c * PX, y: 0 };
    const C = { x: b * PX * Math.cos(toRad(alpha)), y: -b * PX * Math.sin(toRad(alpha)) };
    const texte = `<p>Construis en vraie grandeur le triangle ${nA}${nB}${nC} tel que `
        + `${nA}${nB} = <b>${c} cm</b>, ${nA}${nC} = <b>${b} cm</b> et l'angle ${ang(nB, nA, nC)} `
        + `mesure <b>${alpha}°</b>. Le côté [${nA}${nB}] est déjà tracé.</p>`
        + palette('règle, rapporteur et compas');
    return assembler({
        points: [{ name: nA, ...A }, { name: nB, ...B }],
        preShapes: [{ type: 'line', from: nA, to: nB }],
        tasks: [
            tacheRay('ray_A', nA, alpha,
                `Place le rapporteur sur ${nA}, aligne sa base sur [${nA}${nB}), puis clique à <b>${alpha}°</b>.`),
            tacheArc('arc_A', A, b,
                `Trace en <b>bleu</b> l'arc de centre ${nA} et de rayon <b>${b} cm</b>.`),
            {
                ...tachePoint('point_C', C, nC,
                    `Place en <b>vert</b> le sommet ${nC} à l'intersection de l'arc et de la demi-droite.`),
                extraShapes: [{ type: 'segment', p1: A, p2: C, color: COL_V }],
            },
            tacheSegment('seg_BC', B, C, `Trace en <b>vert</b> le côté [${nB}${nC}] pour fermer le triangle.`),
        ],
        texte,
        label: L_SAS,
        key: 'sas_programme_construction',
        params: {
            niveau: 1,
            triangle_particulier: false,
            longueur_d_un_cote: [a, b, c].map(arr2),
            mesure_d_un_angle_donne: [alpha],
            nombre_de_donnees_fournies: 3,
            nombre_d_invariants_verifies: 3,
            invariants: ['cote_AB_pretrace', 'angle_en_A', 'rayon_AC'],
        },
    });
};

const genSasQcm = () => {
    const { b, c, alpha } = tirerSAS();
    const [nA, nB, nC] = pickLetters(3);
    const options = [
        { choix: `L'angle ${ang(nB, nA, nC)} : son sommet ${nA} est commun aux deux côtés donnés.`, code: null },
        { choix: `L'angle ${ang(nA, nB, nC)} : il porte les lettres ${nA} et ${nB} du premier côté donné.`, code: E_SOMMET },
        { choix: `L'angle ${ang(nA, nC, nB)} : il est opposé au côté [${nA}${nB}].`, code: E_ANGLE_OPPOSE },
        { choix: 'Peu importe lequel des trois : ils donnent tous le même triangle.', code: E_ANGLE_OPPOSE },
    ];
    const texte = `<p>On veut construire le triangle ${nA}${nB}${nC} tel que ${nA}${nB} = ${c} cm, `
        + `${nA}${nC} = ${b} cm et qu'un angle mesure ${alpha}°. Pour que ces trois données `
        + 'déterminent le triangle sans aucun calcul, de quel angle doit-il s\'agir ?</p>';
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_SAS));
    return meta(payload, 'sas_qcm_unique', {
        niveau: 1,
        triangle_particulier: false,
        mesure_d_un_angle_donne: [alpha],
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — sss (niveau 2) : trois longueurs
// ═══════════════════════════════════════════════════════════

/** Triplets d'entiers de 3 à 9 cm, deux à deux distants d'au moins 1 cm, avec marge. */
const TRIPLETS_SSS = (() => {
    const out = [];
    for (let p = LONG_MIN; p <= LONG_MAX; p++) {
        for (let q = p + ECART_LONG; q <= LONG_MAX; q++) {
            for (let r = q + ECART_LONG; r <= LONG_MAX; r++) {
                if (p + q >= r + MARGE_INEG) out.push([p, q, r]);
            }
        }
    }
    return out;
})();

/** a = BC (base tracée), c = AB, b = AC — les trois côtés d'un triplet, mélangés. */
const tirerSSS = () => {
    const t = TRIPLETS_SSS[randInt(0, TRIPLETS_SSS.length - 1)];
    const ordres = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]];
    const [i, j, k] = randomChoice(ordres);
    return { a: t[i], c: t[j], b: t[k] };
};

const genSssConstruction = () => {
    const { a, b, c } = tirerSSS();
    const [nA, nB, nC] = pickLetters(3);
    const B = { x: 0, y: 0 };
    const C = { x: a * PX, y: 0 };
    // Angle en B par la loi des cosinus : le sommet A est placé au-dessus de [BC].
    const cosB = (a * a + c * c - b * b) / (2 * a * c);
    const beta = Math.acos(Math.max(-1, Math.min(1, cosB)));
    const A = { x: c * PX * Math.cos(beta), y: -c * PX * Math.sin(beta) };
    const texte = `<p>Construis en vraie grandeur le triangle ${nA}${nB}${nC} tel que `
        + `${nB}${nC} = <b>${a} cm</b>, ${nA}${nB} = <b>${c} cm</b> et ${nA}${nC} = <b>${b} cm</b>. `
        + `Le côté [${nB}${nC}] est déjà tracé.</p>`
        + `<p>Les deux arcs se coupent en <b>deux</b> points, de part et d'autre de la droite `
        + `(${nB}${nC}) : les deux donnent un triangle juste. Prends celui qui est <b>au-dessus</b> `
        + `de la droite (${nB}${nC}).</p>`
        + palette('règle et compas (pas de rapporteur)');
    return assembler({
        points: [{ name: nB, ...B }, { name: nC, ...C }],
        preShapes: [{ type: 'line', from: nB, to: nC }],
        tasks: [
            tacheArc('arc_B', B, c,
                `Trace en <b>bleu</b> l'arc de centre ${nB} et de rayon <b>${c} cm</b> (la longueur ${nA}${nB}).`),
            tacheArc('arc_C', C, b,
                `Trace en <b>bleu</b> l'arc de centre ${nC} et de rayon <b>${b} cm</b> (la longueur ${nA}${nC}).`),
            tachePoint('point_A', A, nA,
                `Place en <b>vert</b> le sommet ${nA} à l'intersection des deux arcs, au-dessus de la droite (${nB}${nC}).`),
            tacheSegment('seg_BA', B, A, `Trace en <b>vert</b> le côté [${nB}${nA}].`),
            tacheSegment('seg_CA', C, A, `Trace en <b>vert</b> le côté [${nC}${nA}] pour fermer le triangle.`),
        ],
        texte,
        label: L_SSS,
        key: 'sss_programme_construction',
        params: {
            niveau: 2,
            triangle_particulier: false,
            longueur_d_un_cote: [a, b, c],
            nombre_de_donnees_fournies: 3,
            nombre_d_invariants_verifies: 3,
            invariants: ['cote_BC_pretrace', 'rayon_AB', 'rayon_AC'],
            erreurVisee: E_UNE_SOLUTION,
        },
    });
};

const genSssQcm = () => {
    const { a, b, c } = tirerSSS();
    const [nA, nB, nC] = pickLetters(3);
    const options = [
        { choix: `Deux : les arcs se coupent de part et d'autre de la droite (${nB}${nC}).`, code: null },
        { choix: 'Une seule : trois longueurs ne laissent aucun choix.', code: E_UNE_SOLUTION },
        { choix: `Autant qu'on veut : on place ${nA} à vue, là où le triangle a l'air juste.`, code: E_VUE },
        { choix: 'Aucune tant que les trois côtés ne sont pas tracés.', code: E_NON_FERME },
    ];
    const texte = `<p>Pour construire le triangle ${nA}${nB}${nC} tel que ${nB}${nC} = ${a} cm, `
        + `${nA}${nB} = ${c} cm et ${nA}${nC} = ${b} cm, on trace [${nB}${nC}], puis l'arc de centre `
        + `${nB} et de rayon ${c} cm, puis l'arc de centre ${nC} et de rayon ${b} cm. `
        + `Combien y a-t-il de positions possibles pour le sommet ${nA} ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_SSS));
    return meta(payload, 'sss_qcm_unique', {
        niveau: 2,
        triangle_particulier: false,
        longueur_d_un_cote: [a, b, c],
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — asa (niveau 3) : un côté et les deux angles adjacents
// ═══════════════════════════════════════════════════════════

const REPLI_ASA = { a: 6, beta: 50, gamma: 70 };

/** a = BC, beta en B, gamma en C ; b = AC et c = AB sont calculés (loi des sinus). */
const cotesDepuisASA = (a, beta, gamma) => {
    const s = Math.sin(toRad(beta + gamma));
    return { c: (a * Math.sin(toRad(gamma))) / s, b: (a * Math.sin(toRad(beta))) / s };
};

const tirerASA = () => {
    for (let i = 0; i < 400; i++) {
        const a = randInt(LONG_MIN, LONG_MAX);
        const beta = randInt(ANG_MIN, ANG_MAX);
        const gamma = randInt(ANG_MIN, ANG_MAX);
        const somme = beta + gamma;
        if (somme < SOMME_MIN || somme > SOMME_MAX) continue;
        const alpha = 180 - somme;
        if (beta === gamma || alpha === beta || alpha === gamma) continue;  // jamais isocèle
        const { b, c } = cotesDepuisASA(a, beta, gamma);
        if (!cotesValides(a, b, c)) continue;
        return { a, beta, gamma, alpha, b, c };
    }
    const { a, beta, gamma } = REPLI_ASA;
    return { a, beta, gamma, alpha: 180 - beta - gamma, ...cotesDepuisASA(a, beta, gamma) };
};

/**
 * Construction par deux demi-droites issues de la base [BC] (méthode ASA).
 * `consigneGamma` permet de ne PAS redonner la valeur d'un angle calculé.
 */
const constructionParAngles = ({
    a, beta, gamma, nA, nB, nC, texte, label, key, params, consigneBeta, consigneGamma,
}) => {
    const B = { x: 0, y: 0 };
    const C = { x: a * PX, y: 0 };
    const { c } = cotesDepuisASA(a, beta, gamma);
    const A = { x: c * PX * Math.cos(toRad(beta)), y: -c * PX * Math.sin(toRad(beta)) };
    return assembler({
        points: [{ name: nB, ...B }, { name: nC, ...C }],
        preShapes: [{ type: 'line', from: nB, to: nC }],
        tasks: [
            tacheRay('ray_B', nB, beta, consigneBeta),
            tacheRay('ray_C', nC, 180 - gamma, consigneGamma),
            tachePoint('point_A', A, nA,
                `Place en <b>vert</b> le sommet ${nA} à l'intersection des deux demi-droites.`),
            tacheSegment('seg_BA', B, A, `Trace en <b>vert</b> le côté [${nB}${nA}].`),
            tacheSegment('seg_CA', C, A, `Trace en <b>vert</b> le côté [${nC}${nA}] pour fermer le triangle.`),
        ],
        texte,
        label,
        key,
        params,
    });
};

const genAsaConstruction = () => {
    const { a, beta, gamma, b, c } = tirerASA();
    const [nA, nB, nC] = pickLetters(3);
    const texte = `<p>Construis en vraie grandeur le triangle ${nA}${nB}${nC} tel que `
        + `${nB}${nC} = <b>${a} cm</b>, l'angle ${ang(nA, nB, nC)} mesure <b>${beta}°</b> et `
        + `l'angle ${ang(nA, nC, nB)} mesure <b>${gamma}°</b>. Le côté [${nB}${nC}] est déjà tracé.</p>`
        + `<p>Les deux demi-droites se tracent <b>du même côté</b> de (${nB}${nC}) : sinon elles ne `
        + 'se coupent pas là où il faut.</p>'
        + palette('règle et rapporteur (pas de compas)');
    return constructionParAngles({
        a, beta, gamma, nA, nB, nC, texte, label: L_ASA, key: 'asa_programme_construction',
        consigneBeta: `Place le rapporteur sur ${nB}, aligne sa base sur [${nB}${nC}), puis clique à <b>${beta}°</b>.`,
        consigneGamma: `Place le rapporteur sur ${nC}, aligne sa base sur [${nC}${nB}), puis clique à <b>${gamma}°</b>, du même côté.`,
        params: {
            niveau: 3,
            triangle_particulier: false,
            longueur_d_un_cote: [a, b, c].map(arr2),
            mesure_d_un_angle_donne: [beta, gamma],
            somme_des_deux_angles_donnes: beta + gamma,
            nombre_de_donnees_fournies: 3,
            nombre_d_invariants_verifies: 3,
            invariants: ['cote_BC_pretrace', 'angle_en_B', 'angle_en_C'],
        },
    });
};

const genAsaQcm = () => {
    const { a, beta, gamma } = tirerASA();
    const [nA, nB, nC] = pickLetters(3);
    const options = [
        {
            choix: `Tracer [${nB}${nC}] de ${a} cm, puis les deux demi-droites, à ${beta}° en ${nB} et `
                + `à ${gamma}° en ${nC}, du même côté de (${nB}${nC}) : ${nA} est leur point d'intersection.`,
            code: null,
        },
        {
            choix: `Tracer [${nB}${nC}] de ${a} cm, puis une demi-droite de chaque côté de (${nB}${nC}).`,
            code: E_DEMI_DROITES,
        },
        {
            choix: `Tracer [${nA}${nB}] de ${a} cm, puis reporter ${beta}° en ${nA} et ${gamma}° en ${nB}.`,
            code: E_LONGUEUR,
        },
        {
            choix: `Tracer [${nB}${nC}] de ${a} cm, puis placer ${nA} à vue pour que les angles aient l'air justes.`,
            code: E_VUE,
        },
    ];
    const texte = `<p>On veut construire le triangle ${nA}${nB}${nC} tel que ${nB}${nC} = ${a} cm, `
        + `l'angle ${ang(nA, nB, nC)} mesure ${beta}° et l'angle ${ang(nA, nC, nB)} mesure ${gamma}°. `
        + 'Quelle marche à suivre donne ce triangle ?</p>';
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_ASA));
    return meta(payload, 'asa_qcm_unique', {
        niveau: 3,
        triangle_particulier: false,
        mesure_d_un_angle_donne: [beta, gamma],
        somme_des_deux_angles_donnes: beta + gamma,
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — construire (niveau 4) : un côté, un angle adjacent, un angle opposé
// ═══════════════════════════════════════════════════════════

const REPLI_AAS = { a: 6, beta: 50, alpha: 60 };

/** a = BC, beta en B (adjacent à [BC]), alpha en A (opposé) ; gamma est à calculer. */
const tirerAAS = () => {
    for (let i = 0; i < 400; i++) {
        const a = randInt(LONG_MIN, LONG_MAX);
        const beta = randInt(ANG_MIN, ANG_MAX);
        const alpha = randInt(ANG_MIN, ANG_MAX);
        const somme = alpha + beta;
        if (somme < SOMME_MIN || somme > SOMME_MAX) continue;
        const gamma = 180 - somme;
        if (gamma < ANG_MIN || gamma > ANG_MAX) continue;
        if (alpha === beta || alpha === gamma || beta === gamma) continue;  // jamais isocèle
        const { b, c } = cotesDepuisASA(a, beta, gamma);
        if (!cotesValides(a, b, c)) continue;
        return { a, beta, alpha, gamma, b, c };
    }
    const { a, beta, alpha } = REPLI_AAS;
    const gamma = 180 - alpha - beta;
    return { a, beta, alpha, gamma, ...cotesDepuisASA(a, beta, gamma) };
};

const genConstruireTexte = () => {
    const { a, beta, alpha, gamma } = tirerAAS();
    const [nA, nB, nC] = pickLetters(3);
    const texte = `<p>On veut construire le triangle ${nA}${nB}${nC} tel que ${nB}${nC} = ${a} cm, `
        + `l'angle ${ang(nB, nA, nC)} mesure ${alpha}° et l'angle ${ang(nA, nB, nC)} mesure ${beta}°. `
        + `Avant de construire, il faut connaître l'angle ${ang(nA, nC, nB)}, adjacent au côté donné. `
        + 'Quelle est sa mesure ? (Donne un nombre entier de degrés.)</p>';
    const payload = makeSaisie(String(gamma), texte, {
        ...opts(L_CONSTRUIRE), strict: true, suffixe: '°',
    });
    return meta(payload, 'construire_texte', {
        niveau: 4,
        triangle_particulier: false,
        mesure_d_un_angle_donne: [alpha, beta],
        somme_des_deux_angles_donnes: alpha + beta,
        nombre_de_donnees_fournies: 3,
        reponse: gamma,
        erreurVisee: E_ANGLE_OPPOSE,
    });
};

const genConstruirePC = () => {
    const { a, beta, alpha, gamma, b, c } = tirerAAS();
    const [nA, nB, nC] = pickLetters(3);
    const texte = `<p>Construis en vraie grandeur le triangle ${nA}${nB}${nC} tel que `
        + `${nB}${nC} = <b>${a} cm</b>, l'angle ${ang(nB, nA, nC)} mesure <b>${alpha}°</b> et `
        + `l'angle ${ang(nA, nB, nC)} mesure <b>${beta}°</b>. Le côté [${nB}${nC}] est déjà tracé.</p>`
        + `<p>L'angle donné en ${nA} est opposé au côté tracé : commence par <b>calculer</b> la mesure `
        + `de l'angle ${ang(nA, nC, nB)}, puis reporte-la.</p>`
        + palette('règle et rapporteur (pas de compas)');
    return constructionParAngles({
        a, beta, gamma, nA, nB, nC, texte, label: L_CONSTRUIRE, key: 'construire_programme_construction',
        consigneBeta: `Place le rapporteur sur ${nB}, aligne sa base sur [${nB}${nC}), puis clique à <b>${beta}°</b>.`,
        consigneGamma: `Place le rapporteur sur ${nC}, aligne sa base sur [${nC}${nB}), puis clique à l'angle `
            + `${ang(nA, nC, nB)} <b>que tu as calculé</b>, du même côté.`,
        params: {
            niveau: 4,
            triangle_particulier: false,
            longueur_d_un_cote: [a, b, c].map(arr2),
            mesure_d_un_angle_donne: [alpha, beta],
            somme_des_deux_angles_donnes: alpha + beta,
            nombre_de_donnees_fournies: 3,
            nombre_d_invariants_verifies: 3,
            invariants: ['cote_BC_pretrace', 'angle_en_B', 'angle_calcule_en_C'],
            erreurVisee: E_ANGLE_OPPOSE,
        },
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — aug (niveau 5) : triangle particulier, données réduites
// ═══════════════════════════════════════════════════════════

const REPLI_ISO = { theta: 40, base: 4 };

/** Isocèle en A : angle au sommet theta (pair), base GU ; angles à la base entiers. */
const tirerIsocele = () => {
    for (let i = 0; i < 400; i++) {
        const theta = 2 * randInt(Math.ceil(ANG_MIN / 2), Math.floor(ANG_MAX / 2));
        const base = randInt(LONG_MIN, LONG_MAX);
        const angleBase = (180 - theta) / 2;
        if (angleBase < ANG_MIN || angleBase > ANG_MAX) continue;
        const cote = (base / 2) / Math.sin(toRad(theta / 2));
        if (!cotesValides(base, cote, cote, true)) continue;
        return { theta, base, angleBase, cote };
    }
    const { theta, base } = REPLI_ISO;
    return {
        theta, base, angleBase: (180 - theta) / 2,
        cote: (base / 2) / Math.sin(toRad(theta / 2)),
    };
};

const genAugPC = () => {
    const equilateral = randInt(1, 3) === 1;
    if (equilateral) {
        const cote = randInt(LONG_MIN, LONG_MAX);
        const U = { x: 0, y: 0 };
        const G = { x: cote * PX, y: 0 };
        const A = { x: (cote * PX) / 2, y: -(cote * PX * Math.sqrt(3)) / 2 };
        const texte = '<p>Construis en vraie grandeur le triangle <b>équilatéral</b> AUG de côté '
            + `<b>${cote} cm</b>. Le côté [UG] est déjà tracé.</p>`
            + '<p>Le codage « équilatéral » remplace les autres données : les trois côtés ont la même '
            + 'longueur.</p>'
            + palette('règle et compas (pas de rapporteur)');
        return assembler({
            points: [{ name: 'U', ...U }, { name: 'G', ...G }],
            preShapes: [{ type: 'line', from: 'U', to: 'G' }],
            tasks: [
                tacheArc('arc_U', U, cote, `Trace en <b>bleu</b> l'arc de centre U et de rayon <b>${cote} cm</b>.`),
                tacheArc('arc_G', G, cote, `Trace en <b>bleu</b> l'arc de centre G et de rayon <b>${cote} cm</b>.`),
                tachePoint('point_A', A, 'A',
                    'Place en <b>vert</b> le sommet A à l\'intersection des deux arcs, au-dessus de la droite (UG).'),
                tacheSegment('seg_UA', U, A, 'Trace en <b>vert</b> le côté [UA].'),
                tacheSegment('seg_GA', G, A, 'Trace en <b>vert</b> le côté [GA] pour fermer le triangle.'),
            ],
            texte,
            label: L_AUG,
            key: 'aug_programme_construction',
            params: {
                niveau: 5,
                triangle_particulier: true,
                famille: 'equilateral',
                longueur_d_un_cote: [cote, cote, cote],
                nombre_de_donnees_fournies: 2,
                nombre_d_invariants_verifies: 3,
                invariants: ['cote_UG_pretrace', 'rayon_AU', 'rayon_AG'],
            },
        });
    }
    const { theta, base, angleBase, cote } = tirerIsocele();
    const texte = '<p>Construis en vraie grandeur le triangle AUG, <b>isocèle en A</b>, tel que '
        + `l'angle ${ang('U', 'A', 'G')} mesure <b>${theta}°</b> et GU = <b>${base} cm</b>. `
        + 'Le côté [GU] est déjà tracé.</p>'
        + '<p>Les angles en G et en U ne sont pas donnés : le codage « isocèle en A » dit qu\'ils sont '
        + '<b>égaux</b>. Commence par <b>calculer</b> leur mesure.</p>'
        + palette('règle et rapporteur (pas de compas)');
    return constructionParAngles({
        a: base, beta: angleBase, gamma: angleBase, nA: 'A', nB: 'G', nC: 'U', texte,
        label: L_AUG, key: 'aug_programme_construction',
        consigneBeta: 'Place le rapporteur sur G, aligne sa base sur [GU), puis clique à l\'angle à la base '
            + '<b>que tu as calculé</b>.',
        consigneGamma: 'Place le rapporteur sur U, aligne sa base sur [UG), puis clique au <b>même</b> angle, '
            + 'du même côté.',
        params: {
            niveau: 5,
            triangle_particulier: true,
            famille: 'isocele',
            longueur_d_un_cote: [base, arr2(cote), arr2(cote)],
            mesure_d_un_angle_donne: [theta],
            nombre_de_donnees_fournies: 3,
            nombre_d_invariants_verifies: 3,
            invariants: ['cote_GU_pretrace', 'angle_calcule_en_G', 'angle_calcule_en_U'],
        },
    });
};

const genAugQcm = () => {
    const { theta, base } = tirerIsocele();
    const options = [
        {
            choix: 'Calculer les angles en G et en U, égaux, à partir de l\'angle en A, puis les reporter au rapporteur.',
            code: null,
        },
        { choix: `Reporter directement ${theta}° en G et en U : ce sont les angles adjacents à [GU].`, code: E_ANGLE_OPPOSE },
        { choix: 'Placer A à vue, à égale distance de G et de U.', code: E_VUE },
        { choix: 'Placer les trois sommets A, U et G : le triangle est alors construit.', code: E_NON_FERME },
    ];
    const texte = '<p>AUG est un triangle isocèle en A : l\'angle '
        + `${ang('U', 'A', 'G')} mesure ${theta}° et GU = ${base} cm. On commence la construction en `
        + 'traçant le côté [GU]. Que faut-il faire ensuite ?</p>';
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_AUG));
    return meta(payload, 'aug_qcm_unique', {
        niveau: 5,
        triangle_particulier: true,
        mesure_d_un_angle_donne: [theta],
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — rectangle (niveau 6) : l'angle droit est codé
// ═══════════════════════════════════════════════════════════

/** Couples de côtés de l'angle droit dont l'hypoténuse reste sous 9 cm. */
const COUPLES_RECT = (() => {
    const out = [];
    for (let x = LONG_MIN; x <= LONG_MAX; x++) {
        for (let y = x; y <= LONG_MAX; y++) {
            if (Math.hypot(x, y) <= LONG_MAX + 1e-9) out.push([x, y]);
        }
    }
    return out;
})();

const genRectanglePC = () => {
    const [p, q] = COUPLES_RECT[randInt(0, COUPLES_RECT.length - 1)];
    const [c, b] = randInt(0, 1) === 0 ? [p, q] : [q, p];   // c = AB, b = AC
    const hyp = Math.hypot(b, c);
    const [nA, nB, nC] = pickLetters(3);
    const A = { x: 0, y: 0 };
    const B = { x: c * PX, y: 0 };
    const C = { x: 0, y: -b * PX };
    const texte = `<p>Construis en vraie grandeur le triangle ${nA}${nB}${nC}, <b>rectangle en ${nA}</b>, `
        + `tel que ${nA}${nB} = <b>${c} cm</b> et ${nA}${nC} = <b>${b} cm</b>. `
        + `Le côté [${nA}${nB}] est déjà tracé.</p>`
        // La consigne annoncait l'equerre — trois fois — alors que la palette
        // interactive n'en propose pas : une tache `kind: 'angle_ray'` monte TOUJOURS
        // un rapporteur, et aucun outil equerre n'existe dans les composants eleve.
        // L'eleve lisait donc « jamais au rapporteur » et recevait un rapporteur
        // (retour prof 2026-09-26). Arbitrage du prof : aligner la consigne sur
        // l'outil reellement servi.
        // Ce qui reste a faire un jour : cet outil equerre. Sur papier, un angle droit
        // CODE se construit a l'equerre, et la spec 955 le dit — « l'equerre pour un
        // angle droit ». Tant qu'il n'existe pas, on ne le promet pas a l'eleve.
        + `<p>L'angle droit n'est pas donné par une mesure : il est <b>codé</b> sur la figure. `
        + 'C\'est à toi de le construire.</p>'
        + palette('règle, rapporteur et compas');
    return assembler({
        points: [{ name: nA, ...A }, { name: nB, ...B }],
        preShapes: [{ type: 'line', from: nA, to: nB }],
        tasks: [
            tacheRay('ray_A', nA, 90,
                `Place le rapporteur sur ${nA}, aligne sa base sur [${nA}${nB}), puis clique à <b>90°</b> pour tracer la perpendiculaire.`),
            tacheArc('arc_A', A, b,
                `Trace en <b>bleu</b> l'arc de centre ${nA} et de rayon <b>${b} cm</b>.`),
            {
                ...tachePoint('point_C', C, nC,
                    `Place en <b>vert</b> le sommet ${nC} à l'intersection de l'arc et de la demi-droite.`),
                extraShapes: [{ type: 'segment', p1: A, p2: C, color: COL_V }],
            },
            tacheSegment('seg_BC', B, C, `Trace en <b>vert</b> l'hypoténuse [${nB}${nC}] pour fermer le triangle.`),
        ],
        texte,
        label: L_RECT,
        key: 'rectangle_programme_construction',
        params: {
            niveau: 6,
            triangle_particulier: true,
            longueur_d_un_cote: [b, c, arr2(hyp)],
            nombre_de_donnees_fournies: 3,
            nombre_d_invariants_verifies: 3,
            invariants: ['cote_AB_pretrace', 'angle_droit_code_en_A', 'rayon_AC'],
        },
    });
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    sas_programme_construction: genSasConstruction,
    sas_qcm_unique: genSasQcm,
    sss_programme_construction: genSssConstruction,
    sss_qcm_unique: genSssQcm,
    asa_programme_construction: genAsaConstruction,
    asa_qcm_unique: genAsaQcm,
    construire_texte: genConstruireTexte,
    construire_programme_construction: genConstruirePC,
    aug_qcm_unique: genAugQcm,
    aug_programme_construction: genAugPC,
    rectangle_programme_construction: genRectanglePC,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie', programme_construction: 'Construction',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'sas', label: L_SAS, formatsList: buildFormats('sas') },
    { id: 'sss', label: L_SSS, formatsList: buildFormats('sss') },
    { id: 'asa', label: L_ASA, formatsList: buildFormats('asa') },
    { id: 'construire', label: L_CONSTRUIRE, formatsList: buildFormats('construire') },
    { id: 'aug', label: L_AUG, formatsList: buildFormats('aug') },
    { id: 'rectangle', label: L_RECT, formatsList: buildFormats('rectangle') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    tirerSAS, tirerSSS, tirerASA, tirerAAS, tirerIsocele,
    cotesValides, cotesDepuisASA, TRIPLETS_SSS, COUPLES_RECT,
};
